/**
 * ROLE-RULES.WIDGET
 * Périmètre utilisateurs et périmètre du tableau de bord d'un rôle
 */

import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { faBullseye, faInfoCircle, faPlus, faSitemap, faUser, faUsers } from "@fortawesome/free-solid-svg-icons"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { Role, RoleCondition, RoleRules } from "@/redux/role.types"
import { Population } from "@/redux/population.types"
import { roleUpdateRules } from "@/redux/role.actions"
import Chip from "@/components/chip"
import Tooltip from "@/components/tooltip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SelectPopulationsModal from "@/modals/select-populations.modal"
import SelectFilterNamesModal from "@/modals/select-filter-names.modal"
import RoleChoiceModal from "@/modals/role-choice.modal"
import SelectUsersModal from "@/modals/select-users.modal"
import ModalConfirm from "@/components/modal-confirm"
import { STATUS_LOADED, STATUS_SAVING } from "@/redux/_status.types"

//Les conditions et le perimetre fixe s'editent avec la meme modale de populations
const toPopulations = (conditions: RoleCondition[]) =>
  conditions.map((condition) => new Population({ ...condition, id: `${condition.filterName}_${condition.name}` }))

const toConditions = (populations: Population[]): RoleCondition[] =>
  populations
    .filter((population) => population.filterName)
    .map((population) => ({ filterName: population.filterName!, name: population.name }))

export default function RoleRulesWidget({ role }: { role: Role }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const accountName = useAppSelector((state) => state._session.accountName)
  const managerFilters = useAppSelector((state) => state._session.accountOptions.managerFilters ?? [])
  const chipColor = useAppSelector((state) => state._session.accountColors.cta)
  const [modal, setModal] = useState<"members" | "conditions" | "users" | "managers" | "scope" | "dynamic" | "fixed" | null>(null)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)

  async function save(changes: RoleRules) {
    if (savingRef.current) return false
    savingRef.current = true
    setSaving(true)
    try {
      return Boolean(await dispatch(roleUpdateRules(role, changes)))
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  //Role "Managers": no other members nor scope, the admin confirms before they are removed
  async function saveManagers() {
    if (await save({ managerScope: true, conditions: [], userIds: [], scopeFilterNames: [], scopePopulations: [] })) {
      setModal(null)
    }
  }

  function openModal(name: "members" | "users" | "scope") {
    if (saving || savingRef.current) return
    setModal(name)
  }

  return (
    <>
      {modal === "conditions" && (
        <SelectPopulationsModal
          title={t("roles_members")}
          subtitle={t("roles_members_help")}
          isWithoutDefaultFilters
          isSurveyFiltersIncluded
          selectedPopulations={toPopulations(role.conditions)}
          onClose={() => setModal(null)}
          onNext={(populations: Population[]) => save({ conditions: toConditions(populations) })}
        />
      )}
      {modal === "members" && (
        <RoleChoiceModal
          title={t("roles_members")}
          options={[
            {
              value: "conditions" as const,
              icon: faUsers,
              title: t("roles_members_conditions"),
              help: t("roles_members_conditions_help")
            },
            {
              value: "users" as const,
              icon: faUser,
              title: t("roles_members_users"),
              help: t("roles_members_users_help")
            },
            ...(managerFilters.length > 0
              ? [
                  {
                    value: "managers" as const,
                    icon: faSitemap,
                    title: t("roles_members_managers"),
                    help: t("roles_members_managers_help")
                  }
                ]
              : [])
          ]}
          onClose={() => setModal(null)}
          onSelect={setModal}
        />
      )}
      {modal === "managers" && (
        <ModalConfirm
          status={saving ? STATUS_SAVING : STATUS_LOADED}
          onNo={() => setModal(null)}
          onYes={saveManagers}
          text={t("roles_members_managers_confirm")}
          textBold={t("utils_next_ask")}
        />
      )}
      {modal === "users" && (
        <SelectUsersModal
          title={t("roles_members_users")}
          selectedIds={role.userIds}
          onClose={() => setModal(null)}
          onNext={(userIds) => save({ userIds })}
        />
      )}
      {modal === "scope" && (
        <RoleChoiceModal
          title={t("roles_dashboard_scope")}
          options={[
            {
              value: "dynamic" as const,
              icon: faUsers,
              title: t("roles_scope_dynamic"),
              help: t("roles_scope_dynamic_help")
            },
            {
              value: "fixed" as const,
              icon: faBullseye,
              title: t("roles_scope_fixed"),
              help: t("roles_scope_fixed_help")
            }
          ]}
          footer={t("roles_scope_combination")}
          onClose={() => setModal(null)}
          onSelect={setModal}
        />
      )}
      {modal === "dynamic" && (
        <SelectFilterNamesModal
          selectedNames={role.scopeFilterNames}
          onClose={() => setModal(null)}
          onSave={(scopeFilterNames) => save({ scopeFilterNames })}
        />
      )}
      {modal === "fixed" && (
        <SelectPopulationsModal
          title={t("roles_scope_fixed")}
          subtitle={t("roles_scope_fixed_help")}
          isWithoutDefaultFilters
          isSurveyFiltersIncluded
          selectedPopulations={toPopulations(role.scopePopulations)}
          onClose={() => setModal(null)}
          onNext={(populations: Population[]) => save({ scopePopulations: toConditions(populations) })}
        />
      )}

      {/** Marge negative : le trait separateur va d'un bord a l'autre de la
          carte, le texte reste aligne sur son titre. */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 pb-8 mx-[-30px] px-[30px]"
        style={{ borderTop: "1px solid #F0F0F0" }}>
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h3 style={{ margin: 0 }}>{t("roles_members")}</h3>
            <Tooltip text={t("roles_members_help")} width={280} className="!flex items-center leading-none" usePortal>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="grey-t cursor-help"
                aria-label={t("roles_members_help")}
              />
            </Tooltip>
          </div>
          <div className="chips-list">
            {role.managerScope && (
              <Chip
                icon={faSitemap}
                color={chipColor}
                isOutlineColored
                onDelete={saving ? undefined : () => save({ managerScope: false })}>
                {t("roles_members_managers")}
              </Chip>
            )}
            {role.conditions.map((condition, index) => (
              <Chip
                key={JSON.stringify([condition.filterName, condition.name])}
                color={chipColor}
                isOutlineColored
                onDelete={
                  saving ? undefined : () => save({ conditions: role.conditions.filter((_, i) => i !== index) })
                }>
                {condition.filterName} : {condition.name}
              </Chip>
            ))}
            {role.userIds.length > 0 && (
              <Chip icon={faUser} color={chipColor} isOutlineColored onClick={() => openModal("users")}>
                {t("roles_members_users_count", { count: role.userIds.length, s: role.userIds.length > 1 ? "s" : "" })}
              </Chip>
            )}
            {!role.managerScope && <Chip icon={faPlus} onClick={() => openModal("members")} />}
          </div>
        </section>

        <section style={role.managerScope ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
          <div className="flex items-center gap-2 mb-4">
            <h3 style={{ margin: 0 }}>{t("roles_dashboard_scope")}</h3>
            <Tooltip
              text={t("roles_dashboard_scope_help")}
              width={280}
              className="!flex items-center leading-none"
              usePortal>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="grey-t cursor-help"
                aria-label={t("roles_dashboard_scope_help")}
              />
            </Tooltip>
          </div>
          <div className="chips-list">
            {role.managerScope && <Chip icon={faSitemap}>{t("roles_scope_managers")}</Chip>}
            {/** Aucun attribut : le périmètre est l'organisation entière. */}
            {!role.managerScope && role.scopeFilterNames.length === 0 && role.scopePopulations.length === 0 && (
              <Chip>{accountName}</Chip>
            )}
            {role.scopeFilterNames.map((name) => (
              <Tooltip key={name} text={t("roles_scope_dynamic_help")} usePortal>
                <Chip
                  color="#ff9500"
                  isOutlineColored
                  onDelete={
                    saving
                      ? undefined
                      : () => save({ scopeFilterNames: role.scopeFilterNames.filter((item) => item !== name) })
                  }>
                  {name}
                </Chip>
              </Tooltip>
            ))}
            {role.scopePopulations.map((population, index) => (
              <Tooltip
                key={JSON.stringify([population.filterName, population.name])}
                text={t("roles_scope_fixed_help")}
                usePortal>
                <Chip
                  color={chipColor}
                  isOutlineColored
                  onDelete={
                    saving
                      ? undefined
                      : () =>
                          save({
                            scopePopulations: role.scopePopulations.filter((_, i) => i !== index)
                          })
                  }>
                  {population.filterName} : {population.name}
                </Chip>
              </Tooltip>
            ))}
            {!role.managerScope && <Chip icon={faPlus} onClick={() => openModal("scope")} />}
          </div>
        </section>
      </div>
    </>
  )
}
